import state from "../state/state";
import { exportOpenActivity } from "../actions/activityValidationActions";
import { HCA_ORG_ID } from "../components/dashboard/dashboardRegistry";

// "Download authoring guide for AI" in the authoring Tools menu: bundles the activity JSON authoring guide, the
// generated reference, the JSON Schema and the minimal example (served from public/activity-json, kept current by
// `npm run generate-activity-reference`) into one Markdown file an author can hand to another AI, optionally with the
// open activity appended as a worked example.
export const AUTHORING_GUIDE_FILES = Object.freeze({
	guide: "AUTHORING_GUIDE.md",
	reference: "REFERENCE.md",
	schema: "player-checklist-v2.schema.json",
	minimal: "minimal-activity.json"
});

// Organizations that do not get the AI authoring guide: no "AI Guide" button, no guide in the Tools menu and no
// "Activity JSON with AI authoring guide" export.
const AI_AUTHORING_GUIDE_HIDDEN_ORG_IDS = Object.freeze([HCA_ORG_ID]);

export function isAiAuthoringGuideAvailable(orgId = state.get(["selectedOrg", "id"])) {
	return !AI_AUTHORING_GUIDE_HIDDEN_ORG_IDS.includes(orgId);
}

export function authoringGuideUrl(fileName) {
	return `${process.env.PUBLIC_URL || ""}/activity-json/${fileName}`;
}

export async function fetchAuthoringGuideParts(fetchImpl = typeof fetch === "function" ? fetch : null) {
	if (!fetchImpl) {
		throw new Error("The authoring guide files cannot be fetched in this environment.");
	}
	const entries = await Promise.all(Object.entries(AUTHORING_GUIDE_FILES).map(async ([key, fileName]) => {
		const response = await fetchImpl(authoringGuideUrl(fileName), { cache: "no-store" });
		if (!response || !response.ok) {
			throw new Error(`Could not load ${fileName} (${response ? response.status : "no response"}). Rebuild the Cloud with npm run generate-activity-reference.`);
		}
		return [key, await response.text()];
	}));
	return Object.fromEntries(entries);
}

export function formatCloudVersion(version) {
	if (!version || typeof version !== "object") {
		return "unknown";
	}
	const parts = [version.major, version.minor, version.revision].filter((part) => part !== undefined && part !== null && part !== "");
	const build = version.build && !/\[BUILD_NUMBER\]/.test(String(version.build)) ? ` build ${version.build}` : "";
	return `${parts.join(".") || "unknown"}${build}`;
}

export function activityExampleName(activity) {
	return activity && typeof activity.name === "string" && activity.name.trim() ? activity.name.trim() : "Open activity";
}

export function buildAuthoringGuideBundle({ guide, reference, schema, minimal, activity = null, version = null, generatedAt = new Date(), includeReference = true }) {
	const stamp = generatedAt instanceof Date ? generatedAt.toISOString() : String(generatedAt);
	const parts = [];
	parts.push(`<!-- AmbiFi activity JSON authoring bundle. Exported from AmbiFi Cloud ${formatCloudVersion(version)} on ${stamp}. Source of truth: docs/activity-json in the ambifi-cloud repository. Validate output with: npm run validate-activity -- file.json -->`);
	parts.push("");
	parts.push("# How to use this bundle");
	parts.push("");
	parts.push("You are producing an AmbiFi activity as JSON. Read the guide first, use the reference for every key, and make the result validate against the JSON Schema. The minimal example is the smallest activity that passes validation; copy its shape rather than inventing one." + (activity ? " The last section is a real activity exported from the authoring tool, included as a worked example of the house style." : ""));
	parts.push("");
	parts.push(guide.trim());
	if (includeReference && reference) {
		parts.push("");
		parts.push("---");
		parts.push("");
		parts.push(reference.trim());
	}
	parts.push("");
	parts.push("---");
	parts.push("");
	parts.push("# JSON Schema: player-checklist-v2");
	parts.push("");
	parts.push("```json");
	parts.push(schema.trim());
	parts.push("```");
	parts.push("");
	parts.push("# Minimal activity (passes validation)");
	parts.push("");
	parts.push("```json");
	parts.push(minimal.trim());
	parts.push("```");
	if (activity) {
		parts.push("");
		parts.push(`# Worked example: ${activityExampleName(activity)}`);
		parts.push("");
		parts.push("Exported from the authoring tool as it stood when this bundle was made. Runtime-only keys may be present; the guide's section on runtime keys says which ones never to author.");
		parts.push("");
		parts.push("```json");
		parts.push(JSON.stringify(activity, null, "\t"));
		parts.push("```");
	}
	parts.push("");
	return parts.join("\n");
}

export function authoringGuideFileName(activity = null) {
	if (!activity) {
		return "ambifi-activity-authoring-guide.md";
	}
	const slug = activityExampleName(activity).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 48) || "activity";
	return `ambifi-activity-authoring-guide-with-${slug}.md`;
}

export function saveTextFile(fileName, text, doc = typeof document !== "undefined" ? document : null) {
	if (!doc || typeof Blob === "undefined" || typeof URL === "undefined" || typeof URL.createObjectURL !== "function") {
		throw new Error("Downloads are not available in this environment.");
	}
	const blob = new Blob([text], { type: "text/markdown;charset=utf-8" });
	const url = URL.createObjectURL(blob);
	const anchor = doc.createElement("a");
	anchor.href = url;
	anchor.download = fileName;
	anchor.rel = "noopener";
	doc.body.appendChild(anchor);
	anchor.click();
	doc.body.removeChild(anchor);
	setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// `activity`: an exported activity JSON to append as the worked example (the Authoring list passes the stored
// activity); `includeActivity` without it appends the activity open in the editor.
export async function downloadAuthoringGuide({ includeActivity = false, activity: givenActivity = null, includeReference = true, fetchImpl = undefined } = {}) {
	if (!isAiAuthoringGuideAvailable()) {
		throw new Error("The AI authoring guide is not available for this organization.");
	}
	const parts = await fetchAuthoringGuideParts(fetchImpl);
	const activity = givenActivity || (includeActivity ? exportOpenActivity() : null);
	if ((includeActivity || givenActivity) && !activity) {
		throw new Error("The activity could not be exported; the guide was not downloaded.");
	}
	const text = buildAuthoringGuideBundle({ ...parts, activity, version: state.get(["version"]), includeReference });
	const fileName = authoringGuideFileName(activity);
	saveTextFile(fileName, text);
	return { fileName, bytes: text.length, withActivity: !!activity };
}
